/**
 * First-party usage log for signed-in accounts, written to Supabase.
 *
 * GA4 and its BigQuery export carry everyone — signed out or in, with a device
 * id, the plan and (since analytics-identity.ts) the user id on every event.
 * This log exists for the two things GA4 cannot do: see the paying customers
 * it misses behind an ad blocker, where each one of a handful matters, and be
 * readable by the app itself — a dormancy email, a server-side cap.
 *
 * **Signed-out activity is never recorded here.** It once was, and at 315
 * bytes a row the anonymous firehose would have filled the Free plan's 500 MB
 * (at which the whole database goes read-only) within months, to be trimmed
 * on a schedule — stored only to be deleted. Signed-in activity is a few MB a
 * month. The database enforces the rule too: only `authenticated` may insert.
 *
 * Rows go to `public.usage_events` straight through PostgREST. The browser
 * supplies only the event, the county and a small `meta` object; the database
 * stamps who, when and on which plan from the JWT.
 *
 * **Never put a name, an email or typed search text in `meta`.** Keys, counts,
 * ids and enums only.
 *
 * Failure is always silent. Analytics must never break the product, and every
 * path below swallows its errors.
 */

/**
 * Event names. The database checks only the *format* (`^[a-z][a-z0-9_]+$`), so
 * adding one here needs no migration — a closed list in SQL would reject a
 * whole batch the day a new event shipped ahead of it.
 */
export type UsageEventName =
  | "app_load"
  | "signed_in"
  | "parcel_view"
  | "filter_applied"
  | "export_csv"
  | "parcel_map_download"
  | "parcel_gpx_download"
  | "paywall_hit"
  | "pro_waitlist_click"
  | "property_saved"
  // Written by `workers/mcp`, not the browser: one row per ChatGPT/Claude tool
  // call, with the tool name and row count — never the query.
  | "mcp_tool_call"
  // Written by workers/mcp too: a verified account turned away (not on the
  // internal list, plan, allowance, daily ceiling), at most once per account
  // and reason per ten minutes.
  | "mcp_refused";

type MetaValue = string | number | boolean | null | string[];
export type UsageMeta = Record<string, MetaValue>;

interface QueuedEvent {
  event: UsageEventName;
  county: string | null;
  meta: UsageMeta | null;
  /**
   * The access token *when the event happened*, not when the batch leaves, so
   * a batch that straddles a sign-out or an account switch cannot credit one
   * person's activity to another. `undefined` means auth had not resolved yet;
   * see `setUsageAccessToken`.
   */
  token: string | undefined;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "https://placeholder.supabase.co";
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || "placeholder";
const ENDPOINT = `${SUPABASE_URL}/rest/v1/usage_events`;

const FLUSH_DELAY_MS = 10_000;
const FLUSH_AT = 20;
/** Offline or blocked for a long stretch: keep the newest, never grow without bound. */
const MAX_QUEUE = 200;
/** Server rejects meta over 2 KB — and one oversized row fails the whole batch. */
const MAX_META_CHARS = 1500;
/** keepalive requests share a 64 KB budget per page; stay well inside it. */
const MAX_BATCH = 50;

let queue: QueuedEvent[] = [];
/**
 * `undefined` until the first auth state arrives; `null` once known signed
 * out. The difference matters because `app_load` fires on mount, before
 * `getSession()` has restored anyone — a returning subscriber's heartbeat must
 * wait for their session rather than be dropped as signed-out.
 */
let accessToken: string | null | undefined = undefined;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let listenersInstalled = false;

/**
 * The consent decision published by index.html: true, false, or undefined
 * while a decision is pending (there is no CMP today, so it is always true). Nothing is sent until it is known, and a
 * refusal stops the log — the same flag, and the same rule, Clarity follows.
 */
function consent(): boolean | undefined {
  return typeof window === "undefined" ? false : window.__storageConsent;
}

/** The `sub` (user id) inside a Supabase JWT, or null if it cannot be read. */
function subjectOf(token: string): string | null {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(payload)).sub ?? null;
  } catch {
    return null;
  }
}

function dropEverything(): void {
  queue = [];
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}

function installListeners(): void {
  if (listenersInstalled || typeof window === "undefined") return;
  listenersInstalled = true;

  window.addEventListener("storageconsentchange", () => {
    if (consent() === false) dropEverything();
    else if (consent() === true) void flush();
  });

  // The last events of a visit are the most valuable ones — the export just
  // before leaving, the paywall that made them leave. `visibilitychange` is the
  // only unload signal mobile Safari fires reliably; `pagehide` covers the rest.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flush({ keepalive: true });
  });
  window.addEventListener("pagehide", () => void flush({ keepalive: true }));
}

function scheduleFlush(): void {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, FLUSH_DELAY_MS);
}

async function post(
  rows: object[],
  token: string,
  keepalive: boolean,
): Promise<"ok" | "unauthorized" | "rejected" | "network"> {
  try {
    const res = await fetch(ENDPOINT, {
      method: "POST",
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        // Insert-only: there is no SELECT policy to return the row through.
        Prefer: "return=minimal",
      },
      body: JSON.stringify(rows),
      keepalive,
    });
    if (res.ok) return "ok";
    if (res.status === 401) return "unauthorized";
    return "rejected";
  } catch {
    return "network";
  }
}

/**
 * Send what is queued.
 *
 * Consecutive events sharing a token go out as one request, so a batch that
 * straddles an account switch becomes two requests with two identities rather
 * than one request with the wrong one.
 */
export async function flush(options: { keepalive?: boolean } = {}): Promise<void> {
  const keepalive = !!options.keepalive;
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  const decided = consent();
  if (decided === false) {
    dropEverything();
    return;
  }
  // Pending: hold everything and look again shortly. The consent event will
  // usually beat the timer; the timer covers a browser that cannot fire it.
  if (decided !== true) {
    if (queue.length) scheduleFlush();
    return;
  }

  // Auth still unresolved by the time a batch has to leave (a very fast bounce,
  // or a Supabase outage): there is no account to attribute those to, so they
  // go. GA4 has them either way.
  const pending = queue.filter((e): e is QueuedEvent & { token: string } => typeof e.token === "string");
  queue = [];
  if (!pending.length) return;

  const groups: Array<Array<QueuedEvent & { token: string }>> = [];
  for (const e of pending) {
    const last = groups[groups.length - 1];
    if (last && last[0].token === e.token && last.length < MAX_BATCH) last.push(e);
    else groups.push([e]);
  }

  const retry: QueuedEvent[] = [];
  for (const group of groups) {
    // Every row carries all three keys. PostgREST takes a bulk insert's columns
    // from the first object, so a row missing `meta` would lose it silently.
    const rows = group.map((e) => ({ event: e.event, county: e.county, meta: e.meta }));
    let outcome = await post(rows, group[0].token, keepalive);

    // An expired token — a laptop that slept past the refresh. Supabase has
    // usually refreshed by now; resend with the current token, but only if it
    // belongs to the same person, so a sign-in as someone else in between can
    // never inherit these rows.
    if (outcome === "unauthorized" && typeof accessToken === "string" && accessToken !== group[0].token) {
      const was = subjectOf(group[0].token);
      if (was && was === subjectOf(accessToken)) {
        outcome = await post(rows, accessToken, keepalive);
      }
    }

    // A network failure is worth one more try later; a rejection is bad data
    // and retrying it would loop forever.
    if (outcome === "network" && !keepalive) retry.push(...group);
  }

  if (retry.length) {
    queue = [...retry, ...queue].slice(-MAX_QUEUE);
    scheduleFlush();
  }
}

function sanitizeMeta(meta: UsageMeta | undefined): UsageMeta | null {
  if (!meta) return null;
  try {
    if (JSON.stringify(meta).length > MAX_META_CHARS) return { meta_truncated: true };
  } catch {
    return null;
  }
  return meta;
}

/**
 * Record one usage event for the signed-in account. A no-op when signed out.
 * Cheap and synchronous: it only queues.
 *
 * @param county County slug where there is one — the join key everything else
 *               in this app uses, never the display name.
 */
export function logUsage(
  event: UsageEventName,
  options: { county?: string | null; meta?: UsageMeta } = {},
): void {
  if (typeof window === "undefined") return;
  if (consent() === false) return;
  // Known signed out: GA4 has this, and nothing else should.
  if (accessToken === null) return;

  installListeners();

  queue.push({
    event,
    county: options.county ?? null,
    meta: sanitizeMeta(options.meta),
    token: accessToken,
  });
  if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);

  if (queue.length >= FLUSH_AT) void flush();
  else scheduleFlush();
}

/**
 * Keep the token in step with the Supabase session. Called from every auth
 * state change, so a refreshed token is picked up before the old one expires.
 */
export function setUsageAccessToken(token: string | null): void {
  // The first answer also settles everything logged before it: attributed to
  // the session that was restored, or dropped if there was none.
  if (accessToken === undefined) {
    if (token === null) {
      queue = queue.filter((e) => e.token !== undefined);
    } else {
      for (const e of queue) {
        if (e.token === undefined) e.token = token;
      }
    }
  }
  accessToken = token;
}

/** Reset module state. Tests only. */
export function __resetUsageEventsForTests(): void {
  queue = [];
  accessToken = undefined;
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}
